// Shared state between the Character Studio panel and whatever else wants to
// know what is being posed right now.
//
// The studio is DOM UI: in a game it mounts as a sibling of the `<Canvas>`, not
// inside it, so it cannot render into the host's scene graph directly. The same
// split `MapEditorOverlay` + `useMapEditorStore` already use applies here - the
// panel publishes its live selection, and a host that wants to show the rig in
// its own scene subscribes and renders it there. Studio simply never reads it.

import { create } from 'zustand';

import type { CharacterPose } from '../../content/schema/character';

/** Where the rig being posed should be drawn. */
export type CharacterPreviewMode = 'stage' | 'scene';

export interface CharacterStudioState {
  /** Whether a host-mounted overlay is showing. Studio routes ignore this. */
  enabled: boolean;
  /** Id of the `CharacterDefinition` currently selected, if any. */
  characterId: string | null;
  /** Renderer id for that character, so a host can resolve it without the roster. */
  rendererId: string | null;
  /** The pose as currently edited, including unsaved overrides. */
  activePose: CharacterPose | null;
  /** Neutral turntable stage, or the host's own live scene. */
  previewMode: CharacterPreviewMode;

  setEnabled: (enabled: boolean) => void;
  toggleEnabled: () => void;
  setPreviewMode: (mode: CharacterPreviewMode) => void;
  /** Called by the studio panel as the selection or the edited pose changes. */
  setPreviewTarget: (target: {
    characterId: string | null;
    rendererId: string | null;
    activePose: CharacterPose | null;
  }) => void;
}

export const useCharacterStudioStore = create<CharacterStudioState>((set) => ({
  enabled: false,
  characterId: null,
  rendererId: null,
  activePose: null,
  previewMode: 'stage',

  setEnabled: (enabled) => set({ enabled }),
  toggleEnabled: () => set((state) => ({ enabled: !state.enabled })),
  setPreviewMode: (previewMode) => set({ previewMode }),
  setPreviewTarget: ({ characterId, rendererId, activePose }) =>
    set({ characterId, rendererId, activePose }),
}));
